import type { ItemImpact, SimulationResult } from '../../worker';
import type { InvestmentId, Project } from '@finsim/model';
import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { delay } from 'patronum';
import { createSimWorker, type SimWorker } from '../../worker';
import { $effectiveProject } from '../item-visibility';
import { projectClosed } from '../project';
import { $selectedItem } from '../selection';
import { $shownInvestment, $view } from '../view-settings';

/*
 * Deterministic simulation on every project change (CLAUDE.md "Frontend conventions"):
 * - runs start immediately: edits already commit on Enter/blur (not per keystroke), and a
 *   deterministic run takes ~1 ms, so a debounce would only add latency to the 50 ms budget;
 * - every run carries an increasing runId; results from older runs are ignored, so a slow run
 *   can never overwrite a newer one.
 * The previous result stays on screen while a run is in flight (staleness, not spinners).
 */

let worker: SimWorker | null = null;

export const simulateFx = createEffect(
  async ({
    project,
    runId,
    returns,
  }: {
    project: Project;
    runId: number;
    /** The investment to analyse, if any. */
    returns: InvestmentId | null;
  }): Promise<SimulationResult> => {
    worker ??= createSimWorker();
    try {
      return await worker.api.simulate(project, {
        mode: 'deterministic',
        runId,
        returns: returns ? [returns] : false,
      });
    } catch (error) {
      // A crashed worker is replaced on the next run.
      worker.terminate();
      worker = null;
      throw error;
    }
  },
);

export const runRequested = createEvent();

const $runId = createStore(0).on(runRequested, (n) => n + 1);

export const $result = createStore<SimulationResult | null>(null).reset(projectClosed);
export const $simError = createStore<string | null>(null)
  .on(simulateFx.failData, (_, error) => error.message)
  .reset(simulateFx.done, projectClosed);
export const $isSimulating = simulateFx.pending;

// Loads, edits, undo, redo and mute/solo all flow through the effective project.
sample({
  clock: $effectiveProject.updates,
  filter: (project) => project !== null,
  target: runRequested,
});

// Investment returns are costly (IRR every month), and only the Returns view shows them, one
// investment at a time: only that one is analysed, with one more run when it changes.
const $returnsFor = combine($view, $shownInvestment, (view, id) =>
  view === 'returns' ? id : null,
);
sample({
  clock: $returnsFor.updates,
  source: $effectiveProject,
  filter: (project, id) => id !== null && project !== null,
  target: runRequested,
});

const runStarted = sample({
  clock: $runId.updates,
  source: { project: $effectiveProject, returns: $returnsFor },
  fn: ({ project, returns }, runId) => (project ? { project, runId, returns } : null),
});
sample({ clock: runStarted.filterMap((run) => run ?? undefined), target: simulateFx });

// Auto-recovery: a crashed worker was replaced (see simulateFx); run again once, shortly after.
export const RETRY_DELAY_MS = 300;
const $consecutiveFailures = createStore(0)
  .on(simulateFx.fail, (n) => n + 1)
  .reset(simulateFx.done, projectClosed);
sample({
  clock: delay(simulateFx.fail, RETRY_DELAY_MS),
  source: $consecutiveFailures,
  filter: (failures) => failures === 1,
  target: runRequested,
});

sample({
  clock: simulateFx.doneData,
  source: $runId,
  filter: (latest, result) => result.runId === latest,
  fn: (_, result) => result,
  target: $result,
});

// ── Item impact (inspector) ─────────────────────────────────────────────────
// What the selected item contributes to its variant, recomputed lazily after each run.

export interface ImpactRequest {
  project: Project;
  scenarioId: Parameters<SimWorker['api']['impact']>[1];
  itemId: Parameters<SimWorker['api']['impact']>[2];
  runId: number;
}

export const impactFx = createEffect(async (req: ImpactRequest) => {
  worker ??= createSimWorker();
  return { ...req, impact: await worker.api.impact(req.project, req.scenarioId, req.itemId) };
});

export const $impact = createStore<{
  scenarioId: string;
  itemId: string;
  runId: number;
  impact: ItemImpact | null;
} | null>(null).reset(projectClosed);

const impactWanted = sample({
  clock: [$result, $selectedItem],
  source: { project: $effectiveProject, selected: $selectedItem, result: $result },
  fn: ({ project, selected, result }) =>
    project && selected && result?.runId !== undefined
      ? { project, scenarioId: selected.scenarioId, itemId: selected.itemId, runId: result.runId }
      : null,
});
sample({ clock: impactWanted.filterMap((r) => r ?? undefined), target: impactFx });
sample({
  clock: impactFx.doneData,
  source: { selected: $selectedItem, result: $result },
  filter: ({ selected, result }, done) =>
    selected?.itemId === done.itemId &&
    selected.scenarioId === done.scenarioId &&
    result?.runId === done.runId,
  fn: (_, { scenarioId, itemId, runId, impact }) => ({ scenarioId, itemId, runId, impact }),
  target: $impact,
});
